/** useFocusTrap: focus moves into the panel on open, Tab cycles inside it, Escape calls `onEscape`, and focus returns to the invoker on close. Dialog and Takeover share it. */
import { useEffect, type KeyboardEvent, type RefObject } from "react";

const FOCUSABLE = "a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex='-1'])";

export function useFocusTrap<T extends HTMLElement>(panel: RefObject<T | null>, onEscape?: () => void): (e: KeyboardEvent) => void {
  useEffect(() => {
    const invoker = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const first = panel.current?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel.current)?.focus({ preventScroll: true });
    return () => invoker?.focus({ preventScroll: true });
  }, [panel]);
  return (e) => {
    if (e.key === "Escape" && onEscape !== undefined) { e.stopPropagation(); onEscape(); return; }
    if (e.key !== "Tab" || panel.current === null) return;
    const items = [...panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
    const first = items[0];
    const last = items[items.length - 1];
    if (first === undefined || last === undefined) { e.preventDefault(); panel.current.focus(); return; }
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };
}
